import { useEffect, useState } from 'react'
import { arService, AROverdueRow, ARSendResult, ARBucket } from '../../services/arService'
import { User } from '../../types'
import { supabase } from '../../lib/supabase'
import { hasPermission } from '../../lib/permissions'
import { auditService } from '../../services/auditService'
import ArSmsPanel from './ArSmsPanel'
import InvoicesPanel from './InvoicesPanel'
import './ARRemindersView.css'
import { toast } from '../Toast'

const money = (n: number) => `$${Number(n).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const BUCKET_LABEL: Record<ARBucket, string> = {
  due_soon: 'Due soon',
  overdue_1_3: 'Overdue 1–3d',
  call_4_6: 'Call — 4–6d',
  overdue_7_13: 'Overdue 7–13d · credit suspended',
  frozen_14_29: 'Overdue 14–29d · frozen',
  demand_30_plus: '30+ (admin)',
  none: '—',
}

export default function ARRemindersView({ user }: { user: User }) {
  const canSend = hasPermission(user, 'perm-manage-collections')
  const [callBusy, setCallBusy] = useState<string | null>(null)
  const [rows, setRows] = useState<AROverdueRow[]>([])

  // §5.4 days 4-7: a call, deliberately not automated.
  const callsDue = rows.filter((r) => r.bucket === 'call_4_6')

  const markCalled = async (invoiceId: string) => {
    setCallBusy(invoiceId)
    try {
      await supabase
        .from('invoices')
        .update({ phone_task_created_at: new Date().toISOString() })
        .eq('id', invoiceId)
      toast.saved()
      await load()
    } finally {
      setCallBusy(null)
    }
  }
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState<ARSendResult | null>(null)

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      setRows(await arService.getOverdueSummary())
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load overdue invoices')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const handleSend = async () => {
    setSending(true)
    setError(null)
    setResult(null)
    try {
      const res = await arService.sendDueReminders()
      setResult(res)
      await auditService.log(user, 'ar_reminders_sent', { details: { sent: res.sent, skipped: res.skipped } })
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Send failed')
    } finally {
      setSending(false)
      setConfirming(false)
    }
  }

  const sendable = rows.filter((r) => r.bucket !== 'none' && r.bucket !== 'demand_30_plus' && !r.already_sent && r.buyer_email).length
  const adminCount = rows.filter((r) => r.bucket === 'demand_30_plus').length

  return (
    <div className="ar-reminders">
      <div className="ar-header">
        <div>
          <h2>AR Reminders</h2>
          <p className="ar-sub">
            Buyer payment reminders for overdue invoices, on the framework cadence. Email runs
            here; SMS is sent from the panel below. Re-running never double-sends a step. 30+ day formal demands are held
            for Admin sign-off and are not sent here.
          </p>
        </div>
        <div className="ar-actions">
          {!confirming ? (
            <button className="btn-send" onClick={() => setConfirming(true)} disabled={loading || sending || sendable === 0 || !canSend}>
              {sending ? 'Sending…' : `Send due reminders${sendable ? ` (${sendable})` : ''}`}
            </button>
          ) : (
            <>
              <button className="btn-confirm" onClick={handleSend} disabled={sending}>
                {sending ? 'Sending…' : `Confirm — send ${sendable}`}
              </button>
              <button className="btn-cancel" onClick={() => setConfirming(false)} disabled={sending}>
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {result && (
        <div className="ar-result">
          Sent <strong>{result.sent}</strong> · skipped <strong>{result.skipped}</strong>
          {result.requiresAdminSignoff.length > 0 && (
            <> · <strong>{result.requiresAdminSignoff.length}</strong> need Admin sign-off (30+ days)</>
          )}
          {result.errors.length > 0 && <> · <span className="ar-err-inline">{result.errors.length} error(s)</span></>}
          {result.errors.length > 0 && (
            <ul className="ar-errors">
              {result.errors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {adminCount > 0 && (
        <div className="ar-alert">
          {adminCount} invoice(s) are 30+ days overdue and require an Admin-signed formal demand (framework
          Table 10). These are listed below but were not auto-sent.
        </div>
      )}

      {error && <div className="ar-alert error">{error}</div>}

      {callsDue.length > 0 && (
        <div className="ar-calls">
          <div className="ar-calls-head">
            <h3>Calls due</h3>
            <p>
              Days 4 to 7 of the ladder is a phone call, not another email. Nothing is sent
              automatically here — mark the call once you have made it, and the day-7 formal demand
              goes out on schedule if the invoice is still open.
            </p>
          </div>
          <ul className="ar-call-list">
            {callsDue.map((r) => (
              <li key={r.id}>
                <div>
                  <strong>{r.buyer_name || 'Buyer'}</strong>
                  <span className="ar-call-detail">
                    {r.invoice_number} · {money(r.total_amount)} · {r.days_since_due} days overdue
                    {r.buyer_mobile ? ` · ${r.buyer_mobile}` : ' · no mobile on file'}
                  </span>
                </div>
                <button
                  className="ar-call-btn"
                  disabled={!canSend || !!r.already_sent || callBusy === r.id}
                  onClick={() => markCalled(r.id)}
                >
                  {r.already_sent ? 'Called' : callBusy === r.id ? 'Saving…' : 'Mark called'}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <InvoicesPanel user={user} onChanged={load} />

      <ArSmsPanel user={user} />

      {loading ? (
        <div className="ar-loading">Loading overdue invoices…</div>
      ) : rows.length === 0 ? (
        <div className="ar-alert">No open invoices are due or overdue right now.</div>
      ) : (
        <div className="ar-table-wrap">
          <table className="ar-table">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Buyer</th>
                <th className="num">Amount</th>
                <th>Due</th>
                <th className="num">Days</th>
                <th>Stage</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="mono">{r.invoice_number}</td>
                  <td>
                    {r.buyer_name || '—'}
                    {!r.buyer_email && <span className="ar-noemail"> (no email)</span>}
                    {/* Winnie, Checks_20 Sep §8 — the same flag Sales (AR) shows, on the
                        screen where somebody is about to pick up the phone. */}
                    {r.flagged_amended && (
                      <span
                        className="ar-amended"
                        title={
                          r.flagged_amended_reason
                            ? 'Amended: ' + r.flagged_amended_reason + '. Acknowledge it on Sales (AR).'
                            : 'This sale was amended after the invoice was issued. Acknowledge it on Sales (AR).'
                        }
                      >
                        Amended
                      </span>
                    )}
                  </td>
                  <td className="num">{money(r.total_amount)}</td>
                  <td>{new Date(r.due_date).toLocaleDateString('en-AU')}</td>
                  <td className="num">{r.days_since_due}</td>
                  <td><span className={`bucket b-${r.bucket}`}>{BUCKET_LABEL[r.bucket]}</span></td>
                  <td>
                    {r.bucket === 'demand_30_plus'
                      ? 'Admin sign-off'
                      : r.bucket === 'none'
                      ? '—'
                      : r.already_sent
                      ? 'Sent'
                      : 'Pending'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
